import { createServerFn } from "@tanstack/react-start";

// Public XAUUSD volatility profile: average H1 high-to-low range per UTC
// weekday and hour, built from stored M5 spot bars. Price statistics only.
export type HeatmapDTO = {
  cells: (number | null)[][]; // [weekday 0=Mon..4=Fri][hour 0..23], USD
  samples: number[][];
  days: number;
  from: number | null;
  to: number | null;
};

const LOOKBACK_DAYS = 90;
const PAGE = 1000;
let cache: { at: number; value: HeatmapDTO } | null = null;

export const getVolatilityHeatmap = createServerFn({ method: "GET" }).handler(async (): Promise<HeatmapDTO> => {
  if (cache && Date.now() - cache.at < 60 * 60_000) return cache.value;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const since = Math.floor(Date.now() / 1000) - LOOKBACK_DAYS * 86400;
  const need = Math.ceil((LOOKBACK_DAYS * 288 * 5) / 7 * 1.1);
  const pages = Math.ceil(need / PAGE);
  const results = await Promise.all(
    Array.from({ length: pages }, (_, i) =>
      supabaseAdmin
        .from("otas_m5_bars")
        .select("t, h, l")
        .gte("t", since)
        .order("t", { ascending: false })
        .range(i * PAGE, i * PAGE + PAGE - 1),
    ),
  );
  if (results.some((r) => r.error)) {
    if (cache) return cache.value;
    throw new Error("Volatility read failed");
  }
  // Aggregate M5 into H1 buckets.
  const hours = new Map<number, { h: number; l: number; n: number }>();
  for (const r of results.flatMap((x) => x.data ?? [])) {
    const t = Number(r.t);
    const k = Math.floor(t / 3600) * 3600;
    const b = hours.get(k);
    const h = Number(r.h), l = Number(r.l);
    if (b) { b.h = Math.max(b.h, h); b.l = Math.min(b.l, l); b.n++; }
    else hours.set(k, { h, l, n: 1 });
  }
  const sum = Array.from({ length: 5 }, () => Array(24).fill(0) as number[]);
  const cnt = Array.from({ length: 5 }, () => Array(24).fill(0) as number[]);
  const dayKeys = new Set<number>();
  let from: number | null = null, to: number | null = null;
  for (const [k, b] of hours) {
    if (b.n < 9) continue; // partial hours distort the average
    const d = new Date(k * 1000);
    const wd = (d.getUTCDay() + 6) % 7; // Mon=0
    if (wd > 4) continue;
    sum[wd][d.getUTCHours()] += b.h - b.l;
    cnt[wd][d.getUTCHours()]++;
    dayKeys.add(Math.floor(k / 86400));
    from = from == null ? k : Math.min(from, k);
    to = to == null ? k : Math.max(to, k);
  }
  const value: HeatmapDTO = {
    cells: sum.map((row, w) => row.map((s, h) => (cnt[w][h] >= 3 ? Math.round((s / cnt[w][h]) * 100) / 100 : null))),
    samples: cnt,
    days: dayKeys.size,
    from,
    to,
  };
  cache = { at: Date.now(), value };
  return value;
});
